import { useCallback } from "react";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";

export interface ShareOptions {
  url: string;
  title?: string;
  text?: string;
  /** If true, falls back to clipboard copy when native share is unavailable */
  fallbackToClipboard?: boolean;
}

/**
 * Hook to share content using native share sheet (Capacitor) or Web Share API
 * Works on both iOS app and web, with optional clipboard fallback
 */
export const useNativeShare = () => {
  const canShare = useCallback(() => {
    // Check if Capacitor Share is available (native)
    if (Capacitor.isNativePlatform()) {
      return true;
    }

    // Check if Web Share API is available (web)
    if (typeof navigator !== "undefined" && !!navigator.share) {
      return true;
    }

    // Check if clipboard is available as fallback
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      return true;
    }

    return false;
  }, []);

  const share = useCallback(async (options: string | ShareOptions) => {
    // Support both string URL and options object
    const shareOptions: ShareOptions =
      typeof options === "string"
        ? { url: options, fallbackToClipboard: true }
        : { fallbackToClipboard: true, ...options };

    const { url, title, text, fallbackToClipboard } = shareOptions;

    try {
      // Use Capacitor Share for native platforms
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          url,
          title,
          text,
        });
        return;
      }

      // Fall back to Web Share API for web
      if (navigator.share) {
        await navigator.share({
          url,
          title,
          text,
        });
        return;
      }

      // If neither is available, try clipboard fallback
      if (fallbackToClipboard) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
        return;
      }

      // No share method available and fallback disabled
      toast.error("Sharing is not supported on this device");
    } catch (error: any) {
      // User cancelled share - this is normal, don't show error
      if (error?.message?.includes("cancelled") || error?.message?.includes("cancel")) {
        return;
      }

      // Clipboard write failed, try fallback
      if (error?.name === "NotAllowedError" && fallbackToClipboard) {
        toast.error("Please grant clipboard permission to copy the link");
        return;
      }

      // Only show error for actual failures
      console.error("Error sharing:", error);
      toast.error("Failed to share");
    }
  }, []);

  return {
    canShare: canShare(),
    share,
  };
};
